import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Calculator, Plus, RefreshCw, Search,
    FileText, CheckCircle2, Clock,
} from 'lucide-react';

import { cashReconciliationApi } from '@/api/cashReconciliation.api';
import { usePermission }          from '@/hooks/usePermission';
import { Permissions }            from '@/constants/permissions';
import { AppRoutes }              from '@/constants/routes';
import { Spinner }                from '@/components/ui/Spinner';
import { ListPager } from '@/components/lists/ListPager';
import { cn }                     from '@/utils/cn';
import type { RootState }         from '@/state/store';

import type {
  CashReconciliationListDto,
  CashReconciliationFilterDto,
} from '@/types/cashReconciliation.types';
import {
  CASH_RECON_STATUS_COLORS,
  CASH_RECON_STATUS_LABELS,
} from '@/types/cashReconciliation.types';

// ── Status icon ───────────────────────────────────────────────────────────────
const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'Posted':    return <CheckCircle2 size={14} className="text-green-500 shrink-0" />;
    case 'Approved':  return <CheckCircle2 size={14} className="text-yellow-500 shrink-0" />;
    case 'Submitted': return <Clock        size={14} className="text-blue-500 shrink-0" />;
    default:          return <FileText     size={14} className="text-slate-400 shrink-0" />;
  }
};

// ── Variance badge ────────────────────────────────────────────────────────────
const VarianceBadge = ({ variance }: { variance: number }) => {
  const isZero = Math.abs(variance) < 0.005;
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold font-mono',
      isZero ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    )}>
      {isZero ? '0.00' : variance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
    </span>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
export const CashReconciliationListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const companyId         = useSelector((s: RootState) => s.session.session?.companyId ?? '');

  const canCreate = hasPermission((Permissions as any).CASH_RECON_CREATE);

  const [items,   setItems]   = useState<CashReconciliationListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [search,      setSearch]      = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');

  const buildFilter = useCallback((): CashReconciliationFilterDto => ({
    companyId,
    status:   statusFilter,
    dateFrom: dateFrom || undefined,
    dateTo:   dateTo   || undefined,
    page,
    pageSize,
  }), [companyId, statusFilter, dateFrom, dateTo, page]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res  = await cashReconciliationApi.getList(buildFilter());
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load cash reconciliations', err);
    } finally {
      setLoading(false);
    }
  }, [buildFilter, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleStatusToggle = (s: string) => {
    setStatusFilter(prev => prev === s ? undefined : s);
    setPage(1);
  };

  const handlePageChange = (n: number) => setPage(n);

  const totalPages = Math.ceil(total / pageSize);

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const fmtAmt = (n: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2 });

  const statuses = ['Draft', 'Submitted', 'Approved', 'Posted'];

  const statusCounts = statuses.reduce<Record<string, number>>((acc, s) => {
    acc[s] = items.filter(i => i.status === s).length;
    return acc;
  }, {});

  const filteredItems = search
    ? items.filter(i =>
        i.reconciliationNumber.toLowerCase().includes(search.toLowerCase()) ||
        (i.shiftCode ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : items;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Calculator size={20} className="text-teal-600 dark:text-teal-400" />
              Cash Reconciliations
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} total records
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.CASH_RECON_NEW as string)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Reconciliation
              </button>
            )}
          </div>
        </div>

        {/* ── Status summary pills ─────────────────────────────────────────── */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {statuses.map(s => {
            const isActive = statusFilter === s;
            const color = {
              Draft:     { bg: 'bg-slate-50 dark:bg-slate-700',   ring: 'ring-slate-200',  text: 'text-slate-600 dark:text-gray-400' },
              Submitted: { bg: 'bg-blue-50 dark:bg-blue-900/20',  ring: 'ring-blue-200',   text: 'text-blue-600 dark:text-blue-400'  },
              Approved:  { bg: 'bg-yellow-50 dark:bg-yellow-900/20', ring: 'ring-yellow-200', text: 'text-yellow-600 dark:text-yellow-400' },
              Posted:    { bg: 'bg-green-50 dark:bg-green-900/20', ring: 'ring-green-200',  text: 'text-green-600 dark:text-green-400' },
            }[s] ?? { bg: 'bg-slate-50 dark:bg-slate-900', ring: 'ring-slate-200', text: 'text-slate-600 dark:text-gray-400' };
            return (
              <button
                key={s}
                onClick={() => handleStatusToggle(s)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg ring-1 transition-all text-left',
                  'hover:ring-2 hover:shadow-sm',
                  color.bg, color.ring,
                  isActive && 'ring-2 shadow-md',
                )}
              >
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-none">{s}</p>
                  <p className={cn('text-sm font-bold leading-tight', color.text)}>{statusCounts[s] ?? 0}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Filters ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search reconciliation # or shift…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <label className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">From</label>
            <input
              type="date" value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5
                         bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
            />
            <label className="text-xs text-slate-500 dark:text-slate-400">To</label>
            <input
              type="date" value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5
                         bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Calculator size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No cash reconciliations found</p>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.CASH_RECON_NEW as string)}
                className="mt-3 text-teal-600 dark:text-teal-400 text-sm hover:underline"
              >
                Create your first reconciliation
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Recon #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Currency</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Shift</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Expected Balance</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actual Counted</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Variance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredItems.map(rec => (
                <tr
                  key={rec.id}
                  onClick={() => navigate((AppRoutes.CASH_RECON_DETAIL as string).replace(':id', rec.id))}
                  className="hover:bg-teal-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
                >
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {fmtDate(rec.reconciliationDate)}
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400 group-hover:text-teal-800">
                    {rec.reconciliationNumber}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                    {rec.currencyCode}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs">
                    {rec.shiftCode ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                    {fmtAmt(rec.expectedClosingBalance)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                    {fmtAmt(rec.actualCountedAmount)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <VarianceBadge variance={rec.variance} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <StatusIcon status={rec.status} />
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        CASH_RECON_STATUS_COLORS[rec.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
                      )}>
                        {CASH_RECON_STATUS_LABELS[rec.status] ?? rec.status}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={handlePageChange}
          noun="reconciliations"
        />
      )}
    </div>
  );
};
